/**
 * d-collection-sort-availability.js
 *
 * Ordena os produtos da coleção colocando disponíveis antes dos esgotados,
 * buscando todas as páginas da coleção e construindo uma paginação virtual
 * no cliente (sem recarregar a página).
 *
 * Adaptado para o tema "Mode" (Clean Canvas) — os cards não usam <li>, e sim
 * o custom element <product-block>, e a paginação/animações seguem as
 * convenções próprias do tema (não as do Dawn).
 *
 * ── Como incluir ────────────────────────────────────────────────────────────
 * Já incluído em sections/main-collection.liquid:
 *
 *   <script src="{{ 'd-collection-sort-availability.js' | asset_url }}" defer></script>
 *
 * ── Pré-requisitos no HTML (já configurados em main-collection.liquid) ─────
 *
 *   1. Grid de produtos com id="product-grid" e os data attributes:
 *        data-id="{{ section.id }}"
 *        data-per-page="{{ section.settings.coll_num_per_page_int }}"
 *        data-total-pages="{{ paginate.pages }}"
 *
 *   2. Container externo com id="ProductGridContainer" — observado via
 *      MutationObserver para detectar re-renders do tema após filtros/sort AJAX
 *      (o tema substitui o innerHTML de [data-ajax-container="products"]).
 *
 *   3. Wrapper de paginação com classe "pagination-wrapper" — onde a paginação
 *      virtual será injetada, reaproveitando as classes nativas do tema
 *      (.pagination-row, .pagination, .pagination__number, a.prev/a.next).
 *
 * ── Marcação de disponibilidade nos cards ────────────────────────────────────
 * Cada filho direto do grid deve ter o data attribute abaixo para que o
 * script saiba quais produtos estão esgotados (já configurado em
 * snippets/product-block.liquid):
 *
 *   data-product-available="{{ product.available }}"
 *   (Shopify renderiza como "true" ou "false")
 *
 * Cards sem esse atributo (ex: blocos promocionais) são tratados como
 * disponíveis e permanecem no início da listagem.
 *
 * ── Contador de produtos (opcional) ─────────────────────────────────────────
 * Para exibir "Mostrando X–Y de Z produtos", adicione o atributo em qualquer
 * elemento da página:
 *
 *   <span data-collection-count></span>
 *
 * ── Constantes ajustáveis ────────────────────────────────────────────────────
 * Se o tema mudar os IDs/seletores padrão, edite as constantes no topo
 * do script (GRID_ID, GRID_CONTAINER_ID, PAGINATION_SEL, MAX_PAGES).
 *
 * ── Comportamento ────────────────────────────────────────────────────────────
 * - Coleção com 1 página: reordena os cards diretamente no DOM (sem fetch).
 * - Coleção com múltiplas páginas: faz fetch paralelo de todas as páginas,
 *   une os cards, ordena e constrói paginação virtual client-side.
 * - Compatível com filtros e ordenação AJAX do tema (detecta re-renders via
 *   MutationObserver e reinicia o sort automaticamente).
 * - Compatível com navegação via browser back/forward (popstate).
 * - Reaciona a animação de scroll do tema (theme.initAnimateOnScroll) após
 *   cada troca de página virtual, já que os cards são recriados via innerHTML.
 * - Em caso de erro no fetch, cai no reorder de DOM como fallback.
 */

(function () {
  'use strict';

  // ─── Selectors (adapt these if the theme changes) ──────────────────────────
  var GRID_ID           = 'product-grid';
  var GRID_CONTAINER_ID = 'ProductGridContainer';
  var PAGINATION_SEL    = '.pagination-wrapper';
  var MAX_PAGES         = 20;

  // ─── State ─────────────────────────────────────────────────────────────────
  var state = {
    cards        : [],   // { html, soldOut }[]
    perPage      : 8,
    sectionId    : '',
    totalPages   : 1,
    initialized  : false,
    fetching     : false,
    generation   : 0,
  };

  // ─── Read metadata from #product-grid data attributes ─────────────────────
  function readMeta() {
    var el = document.getElementById(GRID_ID);
    if (!el) return null;
    return {
      sectionId : el.dataset.id,
      perPage   : parseInt(el.dataset.perPage, 10)   || 24,
      totalPages: parseInt(el.dataset.totalPages, 10) || 1,
    };
  }

  // ─── URL builder — preserves existing filter/sort params ──────────────────
  function buildUrl(page, sectionId) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return window.location.pathname + '?' + params.toString();
  }

  // ─── Fetch all server pages in parallel ───────────────────────────────────
  function fetchAllPages(sectionId, totalPages) {
    var count = Math.min(totalPages, MAX_PAGES);
    var fetches = [];
    for (var i = 1; i <= count; i++) {
      fetches.push(
        fetch(buildUrl(i, sectionId)).then(function (r) { return r.text(); })
      );
    }
    return Promise.all(fetches);
  }

  // ─── Extract product cards from fetched HTML ───────────────────────────────
  // Filtra apenas [data-product-id] (cards de produto reais). O card de heading
  // ("Card in grid") e blocos promocionais se repetem em toda página nativa da
  // coleção — mesclar todas as páginas os duplicaria se não fossem excluídos.
  function extractCards(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('#' + GRID_ID + ' > [data-product-id]')).map(function (el) {
      return {
        html   : el.outerHTML,
        soldOut: el.dataset.productAvailable === 'false',
      };
    });
  }

  // ─── Sort: available first, sold-out last ─────────────────────────────────
  function sortCards(cards) {
    var available = cards.filter(function (c) { return !c.soldOut; });
    var soldOut   = cards.filter(function (c) { return c.soldOut; });
    return available.concat(soldOut);
  }

  // ─── Re-trigger the theme's scroll-reveal animation for freshly inserted cards ─
  function reinitThemeAnimations() {
    if (window.theme && typeof window.theme.initAnimateOnScroll === 'function') {
      window.theme.initAnimateOnScroll();
    }
  }

  // ─── Update count text elements ───────────────────────────────────────────
  function updateCount(pageNum) {
    var total = state.cards.length;
    var start = (pageNum - 1) * state.perPage + 1;
    var end   = Math.min(pageNum * state.perPage, total);
    var text  = 'Mostrando ' + start + '–' + end + ' de ' + total + ' produtos';
    document.querySelectorAll('[data-collection-count]').forEach(function (el) {
      el.textContent = text;
    });
  }

  // ─── Render a page slice into the grid ────────────────────────────────────
  function renderPage(pageNum) {
    var grid = document.getElementById(GRID_ID);
    if (!grid || state.cards.length === 0) return;

    var start = (pageNum - 1) * state.perPage;
    var slice = state.cards.slice(start, start + state.perPage);
    if (slice.length === 0) return;

    grid.innerHTML = slice.map(function (c) { return c.html; }).join('');

    reinitThemeAnimations();
  }

  // ─── Build virtual pagination (no page reload) ─────────────────────────────
  // Reaproveita as classes nativas do tema (ver snippets/pagination-control.liquid)
  // para que a paginação virtual herde o mesmo visual sem CSS adicional.
  function buildPagination(currentPage) {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;

    var totalPages = Math.ceil(state.cards.length / state.perPage);

    if (totalPages <= 1) {
      wrapper.innerHTML = '';
      return;
    }

    var numbers = '';
    for (var i = 1; i <= totalPages; i++) {
      var activeClass = i === currentPage ? ' active' : '';
      var ariaCurrent = i === currentPage ? ' aria-current="page"' : '';
      numbers += '<span class="pagination__number' + activeClass + '">'
        + '<a href="#" data-vs-page="' + i + '"' + ariaCurrent + '>' + i + '</a>'
        + '</span>';
    }

    var prevHtml = '';
    var nextHtml = '';

    if (currentPage > 1) {
      prevHtml = '<a class="prev" href="#" data-vs-page="' + (currentPage - 1) + '">&laquo; Anterior</a>'
        + '<span class="pagination__sep">&middot;</span>';
    }

    if (currentPage < totalPages) {
      nextHtml = '<span class="pagination__sep">&middot;</span>'
        + '<a class="next" href="#" data-vs-page="' + (currentPage + 1) + '">Próxima &raquo;</a>';
    }

    wrapper.innerHTML = '<div class="pagination-row"><div class="pagination">'
      + prevHtml + numbers + nextHtml
      + '</div></div>';

    wrapper.dataset.page = currentPage;
  }

  // ─── Navigate to a virtual page ────────────────────────────────────────────
  function navigateTo(pageNum) {
    renderPage(pageNum);
    buildPagination(pageNum);
    updateCount(pageNum);

    var url = new URL(window.location.href);
    if (pageNum === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNum);
    }
    history.pushState({ vsPage: pageNum }, '', url.toString());

    // Scroll to top of grid container
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (container) {
      var top = container.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  // ─── Attach click handler to the virtual pagination ───────────────────────
  function attachPaginationListeners() {
    var wrapper = document.querySelector(PAGINATION_SEL);
    if (!wrapper) return;
    wrapper.removeEventListener('click', onPaginationClick);
    wrapper.addEventListener('click', onPaginationClick);
  }

  function onPaginationClick(e) {
    var link = e.target.closest('a[data-vs-page]');
    if (!link) return;
    e.preventDefault();
    var page = parseInt(link.dataset.vsPage, 10);
    if (!isNaN(page)) navigateTo(page);
  }

  // ─── In-page DOM reorder (fallback for single-page collections) ───────────
  function reorderDOM() {
    var grid = document.getElementById(GRID_ID);
    if (!grid) return;
    var items    = Array.from(grid.children);
    var available = items.filter(function (el) { return el.dataset.productAvailable !== 'false'; });
    var soldOut   = items.filter(function (el) { return el.dataset.productAvailable === 'false'; });
    available.concat(soldOut).forEach(function (el) { grid.appendChild(el); });
  }

  // ─── Virtual sort orchestrator ────────────────────────────────────────────
  function initVirtualSort() {
    var meta = readMeta();
    if (!meta) return;

    state.sectionId = meta.sectionId;
    state.perPage   = meta.perPage;
    state.totalPages = meta.totalPages;

    // Single-page collection: just reorder in DOM, no fetch needed
    if (meta.totalPages <= 1) {
      reorderDOM();
      return;
    }

    var gen = ++state.generation;
    state.fetching    = false;
    state.initialized = false;
    state.fetching    = true;

    // Hide grid to prevent flash of unsorted content
    var grid = document.getElementById(GRID_ID);
    if (grid) grid.style.visibility = 'hidden';

    fetchAllPages(state.sectionId, state.totalPages)
      .then(function (pages) {
        if (gen !== state.generation) return; // Stale — a newer sort was triggered

        var all = [];
        pages.forEach(function (html) {
          all = all.concat(extractCards(html));
        });

        state.cards       = sortCards(all);
        state.fetching    = false;
        state.initialized = true;

        renderPage(1);
        buildPagination(1);
        updateCount(1);
        attachPaginationListeners();

        if (grid) grid.style.visibility = '';
      })
      .catch(function (err) {
        console.warn('[sort-availability] Fetch failed, falling back to in-page sort.', err);
        if (gen !== state.generation) return;
        state.fetching = false;
        if (grid) grid.style.visibility = '';
        reorderDOM();
      });
  }

  // ─── MutationObserver — detect the theme's facets/sort AJAX updates ──────
  var _debounceTimer = null;

  function watchForUpdates() {
    var container = document.getElementById(GRID_CONTAINER_ID);
    if (!container) return;

    var observer = new MutationObserver(function (mutations) {
      // Only react when #product-grid itself is (re)inserted into the DOM
      var gridInserted = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.id === GRID_ID
            || (typeof n.querySelector === 'function' && !!n.querySelector('#' + GRID_ID));
        });
      });

      if (!gridInserted || state.fetching) return;

      // Debounce: wait for URL (history.pushState) to update after DOM change
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        state.cards       = [];
        state.initialized = false;
        initVirtualSort();
      }, 150);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Handle browser back/forward navigation ───────────────────────────────
  window.addEventListener('popstate', function (e) {
    if (state.initialized && e.state && e.state.vsPage) {
      renderPage(e.state.vsPage);
      buildPagination(e.state.vsPage);
    }
  });

  // ─── Boot ──────────────────────────────────────────────────────────────────
  function boot() {
    if (!document.getElementById(GRID_ID)) return;
    initVirtualSort();
    watchForUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
